import React, {useEffect, useState} from "react";
import {useForm} from "../../../utils/FormUtils";
import {handleApiException, useHttp} from "../../../services/HttpService";
import {getAuflagenSearchEndpoint} from "../../../services/AuflagenService";
import _ from "lodash";
import {changeFieldValueIfPresent} from "../../../utils/ObjectUtils";
import {toBase64QueryParam} from "../../../utils/URLUtils";
import {logger} from "../../../services/LogService";
import EAFieldset from "../../EAFieldset";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import EAButtonBar from "../../EAButtonBar";
import EAButton from "../../EAButton";
import EATable from "../../EATable";
import {createSelectRowComponentColumn} from "../../../utils/ColumnUtils";

/**
 * Attribute in AuflagenFilter
 */
const attr = {
    abwasseranlage: "abwasseranlage",
    art: "art",

    stichtag: "stichtag", //stichtag überschreibt zeitraum
    gueltigAb: "gueltigAb",
    gueltigBis: "gueltigBis",
};
/**
 * Optionen für zeitliche RadioGroup
 */
const option = {
    stichtag: "stichtag",
    zeitraum: "zeitraum",
    alle: "alle",
};

/**
 * Komponente für die Auswertungen von Auflagen
 * @param betriebsId
 * @param gridColumns
 * @param visible
 * @param anlagenOpts
 * @param auflagenartOpts
 * @param onSelectionChange EventHandler, der aufgerufen wird, sobald die Selektion innerhalb dieses Components geändert wird. Bekommt
 *                          die aktuell ausgewählten Elemente übergebenen.
 * @constructor
 */
function AuflagenAuswertenComponent({betriebsId, gridColumns, visible, anlagenOpts, auflagenartOpts, onSelectionChange}) {
    const id = "auflagensuche";
    const {values: filter, setValues: setFilter, formFactory} = useForm({});
    const [zeitOption, setZeitOption] = useState(option.alle);
    const [{data: anforderungen, loading: auflagenLoading}, fetchAuflagen] = useHttp(
        getAuflagenSearchEndpoint(betriebsId, ""),
        {
            manual: true,
        }
    );

    const [selectedItems, setSelectedItems] = useState([]);
    const handleSelectionChange = (items) => {
        onSelectionChange(items);
        setSelectedItems(items);
    }

    const columnsSlize = gridColumns.slice();
    if (gridColumns) {
        let selectionColumn = createSelectRowComponentColumn(id, "id", anforderungen ? anforderungen : [], selectedItems, handleSelectionChange);
        columnsSlize.unshift(selectionColumn);
    }

    /*
     * Setzt die Datumsfelder zurück wenn Option "alle Zeiträume" ausgewählt
     */
    useEffect(() => {
        if (zeitOption !== option.stichtag && filter[attr.stichtag] !== null) {
            setFilter({...filter, [attr.stichtag]: null});
        }
        if (zeitOption !== option.zeitraum && (filter[attr.gueltigAb] !== null || filter[attr.gueltigBis] !== null)) {
            setFilter({...filter, [attr.gueltigAb]: null, [attr.gueltigBis]: null});
        }
    }, [zeitOption, filter, setFilter]);

    /**
     * Setzt die Filter zurück
     */
    function resetFilter() {
        setZeitOption(option.alle);
        setFilter({});
    }

    /**
     * Startet die Suche nach Auflagen anhand des States "filter"
     */
    function reFetch() {
        handleSelectionChange([]);

        const filterToSend = _.cloneDeep(filter);
        changeFieldValueIfPresent(filterToSend, attr.abwasseranlage, "id");
        changeFieldValueIfPresent(filterToSend, attr.art, "id");

        const q = filterToSend ? "q=" + toBase64QueryParam(filterToSend) : "";

        logger.debug("fetching Auflagen");
        fetchAuflagen(getAuflagenSearchEndpoint(betriebsId, q)).catch((e) =>
            handleApiException(e, "Error fetching Auflagen")
        );
    }

    return visible === false ? null : (
        <div className="gridl tabsheet-content-margin">
            <EAFieldset id={id} caption="Filter">
                <div className="gridl" style={{gridTemplateColumns: "1fr 1fr 1.7fr 2.3fr", alignItems: "center"}}>
                    {formFactory.renderAutocomplete(
                        attr.abwasseranlage,
                        id + "-" + attr.abwasseranlage,
                        "Abwasseranlage",
                        anlagenOpts.data,
                        (o) => {
                            return `${o.bezeichnung}(${o.anlagenId})`;
                        },
                        "id",
                        anlagenOpts.loading,
                        false,
                        false,
                        {style: {gridColumn: "span 2"}},
                    )}
                    {formFactory.renderAutocomplete(
                        attr.art,
                        id + "-" + attr.art,
                        "Art der Auflage",
                        auflagenartOpts.data,
                        "value",
                        "id",
                        auflagenartOpts.loading,
                        false
                    )}
                    <RadioGroup
                        aria-label="anonymous"
                        name="anonymous"
                        value={zeitOption}
                        onChange={(event) => setZeitOption(event.target.value)}
                        row
                        style={{gridColumn: "span 3"}}
                    >
                        <FormControlLabel value={option.alle} control={<Radio/>} label="alle Zeiträume"/>
                        <FormControlLabel value={option.zeitraum} control={<Radio/>} label="im Zeitraum"/>
                        <FormControlLabel value={option.stichtag} control={<Radio/>} label="zum Stichtag"/>
                    </RadioGroup>
                    {zeitOption === option.stichtag &&
                    formFactory.renderDatePicker(attr.stichtag, id + "-" + attr.stichtag, "Stichtag", false, {
                        style: {gridColumn: 3, maxWidth: 144},
                    })}
                    {zeitOption === option.zeitraum &&
                    formFactory.renderDatePicker(attr.gueltigAb, id + "-" + attr.gueltigAb, "von", false, {
                        style: {gridColumn: 1},
                    })}
                    {zeitOption === option.zeitraum &&
                    formFactory.renderDatePicker(attr.gueltigBis, id + "-" + attr.gueltigBis, "bis", false, {
                        style: {gridColumn: 2},
                    })}
                    <EAButtonBar style={{gridColumn: 4, gridRow: 3}}>
                        <EAButton id={id + "-reset"} label="Zurücksetzen" onClick={() => resetFilter()}/>
                        <EAButton
                            id={id + "-submit"}
                            label="Suchen"
                            onClick={() => reFetch()}
                            loading={auflagenLoading}
                            disabled={auflagenLoading}
                            color="primary"
                        />
                    </EAButtonBar>
                </div>
            </EAFieldset>
            <EATable
                id={id}
                loading={auflagenLoading}
                initialSortColumn="betrieb"
                columns={columnsSlize}
                data={anforderungen}
                style={{width: "auto"}}
                className={"noHorizontalPadding"}
            />
        </div>
    );
}

export default AuflagenAuswertenComponent;
